import { useEffect, useState } from "react";
import { renderSection } from "components/index";
import {
  AlertInfo,
  Euro,
  Incoming,
  Percentage,
  Pulse,
  Users,
  Wrench,
} from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { Select, SelectItem } from "@heroui/react";
import { Target } from "@phosphor-icons/react";
import { normalizePercentageInput } from "shared/utils/percentage";

interface FinancialsTabProps {
  buildingId: string;
  buildingIsOwned: boolean;
  acquisitionDate: string;
}

export const FinancialsTab = ({
  buildingId,
  acquisitionDate,
  buildingIsOwned,
}: FinancialsTabProps) => {
  const [buildingFinancials, setBuildingFinancials] = useState<any>(null);
  const [selectedYear, setSelectedYear] = useState("current");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [acquisitionEditMode, setAcquisitionEditMode] =
    useState<boolean>(false);
  const [editedBuildingAcquisitionInfo, setEditedBuildingAcquisitionInfo] =
    useState<any>({});
  const [buildingAcquisitionInfo, setBuildingAcquisitionInfo] = useState<any>(
    {},
  );
  const [buildingAcquisitionInfoLoading, setBuildingAcquisitionInfoLoading] =
    useState<boolean>(true);
  const { session } = useSession();

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
  };

  const fetchBuildingAcquisitionInfo = async () => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/building/building-acquisition-info/${buildingId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      const acquisitionData = await res.json();
      console.log(acquisitionData);
      setBuildingAcquisitionInfo(acquisitionData);
      setEditedBuildingAcquisitionInfo(acquisitionData);
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het ophalen van de acquisitiegegevens van het gebouw.",
      );
    } finally {
      setBuildingAcquisitionInfoLoading(false);
    }
  };

  useEffect(() => {
    if (buildingId) fetchBuildingAcquisitionInfo();
  }, [buildingId]);

  const saveBuildingAcquisitionInfo = async () => {
    if (!buildingIsOwned) return; // Only allow saving if building is owned

    const preparedData = {
      ...editedBuildingAcquisitionInfo,
      estimatedYieldResidential: normalizePercentageInput(
        editedBuildingAcquisitionInfo.estimatedYieldResidential,
      ),
      estimatedYieldOfficeStorage: normalizePercentageInput(
        editedBuildingAcquisitionInfo.estimatedYieldOfficeStorage,
      ),
      estimatedYieldRetail: normalizePercentageInput(
        editedBuildingAcquisitionInfo.estimatedYieldRetail,
      ),
    };

    try {
      await fetchWithAuth(
        `${API_URL}/building/building-acquisition-info`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(preparedData),
        },
        session,
      );
      fetchBuildingAcquisitionInfo();
      toast("Gebouw acquisitie info geupdate!");
    } catch (error) {
      console.error("Failed to save building: ", error);
      toast.error("Error bij het opslaan van de gebouw acquisitie info");
    } finally {
      setAcquisitionEditMode(false);
    }
  };

  const toggleAcquisitionEditMode = () => {
    if (!buildingIsOwned) {
      return toast.warning(
        "Je kan de acquisitiegegevens niet bewerken voor een gebouw dat niet in eigendom is aangezien deze dan nog gelinkt zijn aan de acquisitie excel. Zodra het aangekocht is wordt deze informatie losgekoppeld en kan je deze bewerken.",
      );
    }
    setAcquisitionEditMode((prevMode) => !prevMode);
    if (!acquisitionEditMode) {
      setEditedBuildingAcquisitionInfo(buildingAcquisitionInfo || {});
    }
  };

  const getCurrentYearDates = () => {
    const currentDate = new Date();
    const firstDayCurrentYear = new Date(currentDate.getFullYear(), 0, 1)
      .toISOString()
      .split("T")[0];
    const lastDayCurrentYear = currentDate.toISOString().split("T")[0]; // Today, assuming you want up to the current date
    return { startDate: firstDayCurrentYear, endDate: lastDayCurrentYear };
  };

  const getPreviousYearDates = () => {
    const currentDate = new Date();
    const firstDayPreviousYear = new Date(currentDate.getFullYear() - 1, 0, 1)
      .toISOString()
      .split("T")[0];
    const lastDayPreviousYear = new Date(currentDate.getFullYear() - 1, 11, 31)
      .toISOString()
      .split("T")[0];
    return { startDate: firstDayPreviousYear, endDate: lastDayPreviousYear };
  };

  const fetchBuildingFinancials = async (
    buildingId: string,
    startDate?: any,
    endDate?: any,
  ) => {
    setIsLoading(true); // Assuming you have a state to track loading status
    try {
      // Construct the query parameters based on the presence of startDate and endDate
      let queryParams = `buildingId=${encodeURIComponent(buildingId)}`;
      if (startDate) {
        queryParams += `&startDate=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        queryParams += `&endDate=${encodeURIComponent(endDate)}`;
      }

      console.log('🏢 [Financials] Fetching building financials:', {
        buildingId,
        startDate,
        endDate,
        fullUrl: `${API_URL}/building-financials?${queryParams}`
      });

      const res = await fetchWithAuth(
        `${API_URL}/building-financials?${queryParams}`, // Using template literals to include query params
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      const financialsData = await res.json();

      console.log('💰 [Financials] Response received:', {
        fullResponse: financialsData,
        financialsObject: financialsData.financials,
        totalRentalIncome: financialsData.financials?.totalRentalIncome,
        totalRentalIncomeType: typeof financialsData.financials?.totalRentalIncome,
        allFinancialKeys: Object.keys(financialsData.financials || {})
      });

      // Check if totalRentalIncome is missing or 0
      if (!financialsData.financials?.totalRentalIncome || financialsData.financials.totalRentalIncome === 0) {
        console.warn('⚠️ [Financials] totalRentalIncome is 0 or missing!', {
          totalRentalIncome: financialsData.financials?.totalRentalIncome,
          relatedFields: {
            totalNetRentalIncome: financialsData.financials?.totalNetRentalIncome,
            totalProvisions: financialsData.financials?.totalProvisions,
            totalRentExpenditures: financialsData.financials?.totalRentExpenditures,
          }
        });
      }

      setBuildingFinancials(financialsData.financials);
    } catch (e) {
      console.error('❌ [Financials] Error fetching financials:', e);
      toast.error(
        "Er is een fout opgetreden bij het ophalen van de financiële gegevens van het gebouw.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let startDate, endDate;

    if (selectedYear === "current") {
      ({ startDate, endDate } = getCurrentYearDates());
    } else if (selectedYear === "previous") {
      ({ startDate, endDate } = getPreviousYearDates());
    }

    console.log(startDate);
    console.log(endDate);
    // For 'all', no startDate or endDate is set, fetching data without date constraints.

    fetchBuildingFinancials(buildingId, startDate, endDate);
  }, [buildingId, selectedYear]);

  const financialFieldsEvalProperty = [
    {
      label: "Vraagprijs (excl. kosten)",
      key: "grossAskingPrice",
      mask: "valuta",
    },
    {
      label: "Vraagprijs (incl. kosten)",
      key: "netAskingPrice",
      mask: "valuta",
    },
    {
      label: "Bod niet-residentieel",
      key: "bidNonResidential",
      mask: "valuta",
    },
    {
      label: "Bod residentieel",
      key: "valueResidential",
      mask: "valuta",
    },
    {
      label: "Vastgoedprijs (excl. kosten) = Bod Yally",
      key: "totalBid",
      mask: "valuta",
    },
    {
      label: "+ Transactiekosten (RR + Notaris bij akte)",
      key: "transactionCosts",
      mask: "valuta",
    },
    {
      label: "+ Uitgestelde registratiebelastingen (incl. eventuele boetes)",
      key: "deferredRegistrationTaxes",
      mask: "valuta",
    },
    {
      label: "+ Makelaarskosten",
      key: "brokerCostsNet",
      mask: "valuta",
    },
    {
      label: "+ Due diligence kosten",
      key: "dueDiligenceCost",
      mask: "valuta",
    },
    {
      label:
        "Totale verwachte cash-out voor kortingen en excl. renovatiebudget (Aankoopkosten))",
      key: "totalCashOutBeforeRenovationAndDiscounts",
      mask: "valuta",
    },
    {
      label: "- Fiscale korting op de liquidatie van de vennootschap",
      key: "fiscalDiscountCompanyShares",
      mask: "valuta",
    },
    {
      label: "- Fiscale korting op de meerwaarde van het vastgoed",
      key: "fiscalDiscountRealEstate",
      mask: "valuta",
    },
    {
      label:
        "- Andere kortingen (bv. tgv vergunningsproblematiek, huurkortingen, technische staat, ..)",
      key: "otherDiscounts",
      mask: "valuta",
    },
    {
      label:
        "Totale verwachte cash-out na kortingen en excl. renovatiebudget (Aankoopkosten - kortingen)",
      key: "totalCashOutBeforeRenovation",
      mask: "valuta",
    },
    {
      label: "+ Renovatiebudget (incl. btw) bij aankoop",
      key: "renovationBudget",
      mask: "valuta",
    },

    {
      label:
        "Totale verwachte cash-out na korting en incl. renovatiebudget (Aankoopkosten - kortingen + renovatiebudget)",
      key: "totalCashOutIncludingRenovation",
      mask: "valuta",
    },
  ];

  const financialBenchmarks = [
    {
      label:
        "Kostprijs wooneenheid residentieel (excl. transactiekosten en makelaar) (excl. renovatie)",
      key: "bidPerUnitExclRenovation",
      mask: "valuta",
    },
    {
      label:
        "Kostprijs wooneenheid residentieel (excl. transactiekosten en makelaar) (incl. renovatie)",
      key: "bidPerUnitInclRenovation",
      mask: "valuta",
    },
    {
      label:
        "Kostprijs per netto m² residentieel (excl. transactiekosten en makelaar) (excl. renovatie)",
      key: "bidPerSqmExclRenovation",
      mask: "valuta",
    },
    {
      label:
        "Kostprijs per netto m² residentieel (excl. transactiekosten en makelaar) (incl. renovatie)",
      key: "bidPerSqmInclRenovation",
      mask: "valuta",
    },
    {
      label: "Geschatte marktwaarde (uitponding t=0)",
      key: "estimatedMarketValue",
      mask: "valuta",
    },
  ];

  const financialFieldsEvalRent = [
    {
      label: "Bruto huur/jaar actueel bij aankoop (PGI)",
      key: "potentialGrossIncomeActual",
      mask: "valuta",
    },
    {
      label: "Netto huur/jaar actueel bij aankoop (NOI)",
      key: "netOperatingIncomeActual",
      mask: "valuta",
    },
    {
      label: "Netto huurrendement actueel bij aankoop",
      key: "bidPriceActualNetYield",
      mask: "percentage_comma",
    },
    {
      label: "Netto huur/jaar best case bij aankoop (NOI)",
      key: "netOperatingIncomeBestCase",
      mask: "valuta",
    },
    {
      label: "Netto huur/jaar worst case bij aankoop (NOI)",
      key: "netOperatingIncomeWorstCase",
      mask: "valuta",
    },
    {
      label: "Huurrendement best case bij aankoop",
      key: "bidPriceBestCaseNetYield",
      mask: "percentage_comma",
    },
    {
      label: "Huurrendement worst case bij aankoop",
      key: "bidPriceWorstCaseNetYield",
      mask: "percentage_comma",
    },
    {
      label: "Huur per m2/jaar actueel bij aankoop",
      key: "rentActualPerSqm",
      mask: "valuta",
    },
  ];

  const financialFieldsValueProperty = [
    {
      label:
        "Aankoopprijs (incl. transactiekosten en kortingen) (excl. renovatiebudget)",
      key: "totalCashOutBeforeRenovation",
      mask: "valuta",
    },
    {
      label: "Boekwaarde bij aankoop (220000 + 221000)",
      key: "purchaseBookValue",
      mask: "valuta",
    },
    {
      label:
        "Actuele boekwaarde (220000 + 221000 - 221009 + 222000 - 222009 + 222010 - 222019 + 222020 - 222029 + 222040 - 222049 + 222050 - 222059 + 222200 - 222209 + 222210 - 222219 + 221100 - 221109)",
      key: "currentBookValue",
      mask: "valuta",
    },
    {
      label:
        "Renovatiekosten (222000 + 222010 + 222020 + 222040 + 222050 + 222200 + 222210 + 221100) voor deze periode",
      key: "renovationCosts",
      mask: "valuta",
    },
    {
      label: "Renovatiebudget (incl. btw) bij aankoop",
      key: "renovationBudget",
      mask: "valuta",
    },
    {
      label:
        "Renovatiekosten (222000 + 222010 + 222020 + 222040 + 222050 + 222200 + 222210 + 221100) over alle jaren heen (totaal)",
      key: "renovationCostsTotal",
      mask: "valuta",
    },
    {
      label:
        "Totale cash-out na korting en incl. actuele renovatiekosten over alle jaren (Aankoopkosten - kortingen + renovatiekosten over alle jaren heen)",
      key: "totalCashOutInclActualRenovationOverAllYears",
      mask: "valuta",
    },
    {
      label: "Restbudget renovatie over alle jaren heen (totaal)",
      key: "deltaRenovationCostsVsBudget",
      mask: "valuta",
    },
    { label: "Deel verkocht", key: "partSold", mask: "valuta" },
  ];

  const financingFields = [
    {
      label: "Geïnvesteerd eigen vermogen (A)",
      key: "investedOwnCapital",
      mask: "valuta",
    },
    {
      label: "Geïnvesteerde schulden (B)",
      key: "investedDebt",
      mask: "valuta",
    },
    { label: "Openstaande schuld", key: "restDebt", mask: "valuta" },
    { label: "Geïnvesteerd", key: "invested", mask: "valuta" },
    {
      label: "Totaal geïnvesteerd betaald (A+B)",
      key: "totalInvestedPaid",
      mask: "valuta",
    },
  ];

  const financialFieldsIncomingMargins = [
    {
      label:
        "1. Totale huurinkomsten (700000 + 700001 + 700002 + 700003 + 700004 + 700005 + 700006 + 700202) (+)",
      key: "totalRentalIncome",
      mask: "valuta",
      indentation: 2,
    },
    {
      label:
        "2. Provisies (700020 + 700101 + 700102 + 700103 + 700104 + 700105) (+)",
      key: "totalProvisions",
      mask: "valuta",
      indentation: 2,
    },
    {
      label: "3. Huurlasten (611100 + 612010 + 612020 + 612030) (-)",
      key: "totalRentExpenditures",
      mask: "valuta",
      indentation: 2,
    },
    {
      label: "4. Provisie OV - vastgoedbelastingen (700030) (+)",
      key: "totalTaxProvisions",
      mask: "valuta",
      indentation: 2,
    },
    {
      label: "5. OV - vastgoedbelastingen (640360 + 640361) (-)",
      key: "totalTaxExpenditures",
      mask: "valuta",
      indentation: 2,
    },
    {
      label: "I. Netto huurinkomsten - Vastgoedresultaat (= 1 + 2 + 4 - 3 - 5)",
      key: "totalNetRentalIncome",
      mask: "valuta",
      style: "bold",
    },
    {
      label:
        "6. Vastgoedkosten (610060 + 611020 + 611021 + 313100 + 613150 + 600000 + 600010 + 600020 + 600030 + 600040 + 600050 + 602060 + 602071 + 602072) (-)",
      key: "propertyCosts",
      mask: "valuta",
      indentation: 2,
    },
    {
      label: "II. Operationeel vastgoedresultaat (I - 6)",
      key: "marginBeforeVAT",
      mask: "valuta",
      style: "bold",
    },
    {
      label: "Percentage operationele kosten (1 - II / I)",
      key: "percentageOperationalCosts",
      mask: "percentage_comma",
      style: "bold",
    },
    // { label: "Herwaardering van eigendommen", key: "valueOwnProperties" },
    // { label: "Schuldlasten", key: "debts" },
    // { label: "Nettomarge", key: "marginAfterVAT" },
    // { label: "Vrije kasstroom", key: "freeStream" },
  ];

  const buildingComposureFields = [
    { label: "Residentiële units", key: "actualResidentialUnits" },
    { label: "Retail units", key: "actualRetailUnits" },
    { label: "Kantoor units", key: "actualOfficeUnits" },
    { label: "Overdekte parking", key: "acquisitionFileNumParkingsInside" },
    {
      label: "Niet-overdekte parking",
      key: "acquisitionFileNumParkingsOutside",
    },
    { label: "Bergingen", key: "acquisitionFileNumStorages" },
    { label: "Fietsenstallingen", key: "acquisitionFileNumBicycleSheds" },
    {
      label: "Oppervlakte residentieel (bij aankoop)",
      key: "surfaceResidential",
      mask: "surface",
    },
    {
      label: "Netto oppervlakte residentieel obv plan (actueel ingevuld)",
      key: "actualNetSurfaceResidential",
      mask: "surface",
    },
    {
      label: "Oppervlakte niet-residentieel (bij aankoop)",
      key: "surfaceNonResidential",
      mask: "surface",
    },
    {
      label: "Netto oppervlakte niet-residentieel obv plan (actueel ingevuld)",
      key: "actualNetSurfaceNonResidential",
      mask: "surface",
    },
  ];

  const financialFieldsOccupation = [
    {
      label:
        "Percentage verhuurd (actuele huur / (actuele huur + erv leegstand)",
      key: "occupancyRate",
      mask: "percentage_comma",
    },
    {
      label:
        "Gemiddelde leaseperiode (# maanden verhuurd / # aantal maanden verhuurd + # maanden leeg)",
      key: "avgLeasingPeriod",
    },
    {
      label: "Actuele ERV op leegstand op jaarbasis",
      key: "ervOnVacantUnits",
      mask: "valuta",
    },
    {
      label: "Actuele ERV op leegstand op jaarbasis (alleen residentieel)",
      key: "ervOnVacantResidentialUnits",
      mask: "valuta",
    },
    {
      label:
        "Huurverlies op leegstand (ERV van de units pro rata de leegstaande periode)",
      key: "rentalLossOnVacancy",
    },
    {
      label:
        "Theoretische huuropbrengsten per m2 (alleen residentieel) (= (actuele geïndexeerde contractuele huur op jaarbasis + ERV) / som netto oppervlakte per units ) ",
      key: "rentalIncomeResidentialPerSqm",
      mask: "valuta_per_sqm",
    },
  ];

  const financialFieldsROI = [
    {
      label:
        "ROI incl. ERV (totaal investering) (= (Actuele geïndexeerde contractuele huur zonder provisies en incl. ERV) / aankoopprijs incl. kosten en kortingen) + werkelijke renovatiekosten",
      key: "roiTotalInvestmentInclERV",
      mask: "percentage_comma",
    },
    {
      label:
        "ROI excl. ERV (totaal investering) (= (Actuele geïndexeerde contractuele huur zonder provisies en excl. ERV) / aankoopprijs incl. kosten en kortingen) + werkelijke renovatiekosten",
      key: "roiTotalInvestmentExclERV",
      mask: "percentage_comma",
    },
    {
      label:
        "ROI incl. ERV (marktwaarde) (= (Actuele geïndexeerde contractuele huur zonder provisies en incl. ERV) / interne taxatiewaarde)",
      key: "roiMarketValueInclERV",
      mask: "percentage_comma",
    },
    {
      label:
        "ROI excl. ERV (marktwaarde) (= (Actuele geïndexeerde contractuele huur zonder provisies en excl. ERV) / interne taxatiewaarde)",
      key: "roiMarketValueExclERV",
      mask: "percentage_comma",
    },
    {
      label:
        "ROE (actuele geïndexeerde contractuele huur zonder provisies en incl. ERV) / eigen vermogen)",
      key: "ROEInclERV",
      mask: "percentage_comma",
    },
    {
      label:
        "ROE (actuele geïndexeerde contractuele huur zonder provisies en excl. ERV) / eigen vermogen)",
      key: "ROEExclERV",
      mask: "percentage_comma",
    },
    {
      label:
        `Totale return (totale geïnde huur over alle jaren / (aankoopkosten + werkelijke renovatiekosten over alle jaren)) sinds aankoop op ${acquisitionDate}`,
      key: "totalReturn",
      mask: "percentage_comma"
    },
  ];

  const financialFieldsPaymentsTenants = [
    { label: "Te ontvangen van huurders", key: "actualRentalReceivables" },
    { label: "Dagen tot betaling huurder", key: "avgPaymentDelay" },
    { label: "Huurdersvorderingen 30 dagen", key: "tenantsNotices30Days" },
    { label: "Huurdersvorderingen 60 dagen", key: "tenantsNotices60Days" },
    { label: "Huurdersvorderingen 90 dagen", key: "tenantsNotices90Days" },
    { label: "Huurdersvorderingen langer", key: "tenantsNoticesExtended" },
    { label: "Vorderingen gemiddelde looptijd", key: "tenantsNoticesAvg" },
  ];

  const taxationBenchmarkFields = [
    {
      label: "Geschatte yield residentieel",
      key: "estimatedYieldResidential",
      mask: "percentage_comma",
    },
    {
      label: "Geschatte yield kantoren",
      key: "estimatedYieldOfficeStorage",
      mask: "percentage_comma",
    },
    {
      label: "Geschatte yield retail",
      key: "estimatedYieldRetail",
      mask: "percentage_comma",
    },
    {
      label: "Geschatte huurwaarde op residentieel (ERV)",
      key: "ervResidentialUnits",
      mask: "valuta",
    },
    {
      label: "Geschatte huurwaarde op retail (ERV)",
      key: "ervRetailUnits",
      mask: "valuta",
    },
    {
      label: "Geschatte huurwaarde op kantoren (ERV)",
      key: "ervOfficeUnits",
      mask: "valuta",
    },
    {
      label: "Prijs parkeerplaatsen overdekt",
      key: "priceParkingPlacesCovered",
      mask: "valuta",
    },
    {
      label: "Prijs parkeerplaatsen niet overdekt",
      key: "priceParkingPlacesNotCovered",
      mask: "valuta",
    },
    { label: "Prijs bergingen", key: "priceStorage", mask: "valuta" },
    {
      label: "Prijs fietsenstallingen",
      key: "priceBicycleStorage",
      mask: "valuta",
    },
  ];

  const taxationFields = [
    {
      label: "Interne taxatiewaarde residentieel",
      key: "internalTaxationResidential",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde retail",
      key: "internalTaxationRetail",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde kantoren",
      key: "internalTaxationOfficeStorage",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde parkeerplaatsen overdekt",
      key: "internalTaxationParkingsCovered",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde parkeerplaatsen niet-overdekt",
      key: "internalTaxationParkingsNotCovered",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde bergingen",
      key: "internalTaxationStorages",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde fietsenstallingen",
      key: "internalTaxationBicycleSheds",
      mask: "valuta",
    },
    {
      label: "Interne taxatiewaarde totaal",
      key: "internalTaxation",
      mask: "valuta",
    },
    {
      label: "Externe taxatiewaarde totaal",
      key: "externalTaxation",
      mask: "valuta",
    },
  ];

  const actualIndexedContractualRents = [
    {
      label:
        "Actuele contractuele geïndexeerde huuropbrengsten (excl. provisies) residentieel",
      key: "actualIndexedContractualRentsResidential",
      mask: "valuta",
    },
    {
      label:
        "Waarvan residentiële huur in opzegfase, waarvoor nog geen nieuwe huurder is gevonden",
      key: "actualResidentialRentInTerminationPhase",
      mask: "valuta",
    },
    {
      label:
        "Actuele contractuele geïndexeerde huuropbrengsten (excl. provisies) retail",
      key: "actualIndexedContractualRentsRetail",
      mask: "valuta",
    },
    {
      label:
        "Waarvan retail huur in opzegfase, waarvoor nog geen nieuwe huurder is gevonden",
      key: "actualRetailRentInTerminationPhase",
      mask: "valuta",
    },
    {
      label:
        "Actuele contractuele geïndexeerde huuropbrengsten (excl. provisies) kantoren",
      key: "actualIndexedContractualRentsOffices",
      mask: "valuta",
    },
    {
      label:
        "Waarvan kantoor huur in opzegfase, waarvoor nog geen nieuwe huurder is gevonden",
      key: "actualOfficeRentInTerminationPhase",
      mask: "valuta",
    },
    {
      label:
        "Actuele contractuele geïndexeerde huuropbrengsten (excl. provisies) totaal (excl. parkings, stockage, ..)",
      key: "actualIndexedContractualRentsTotal",
      mask: "valuta",
    },
    {
      label:
        "Waarvan totaal huur in opzegfase, waarvoor nog geen nieuwe huurder is gevonden",
      key: "actualTotalRentInTerminationPhase",
      mask: "valuta",
    },
    {
      label: "Actuele ERV op leegstand op jaarbasis",
      key: "ervOnVacantUnits",
      mask: "valuta",
    },
    {
      label: "Actuele contractuele geïndexeerde huuropbrengsten + ERV",
      key: "contractualRentsPlusErv",
      mask: "valuta",
    },
    {
      label: "Bruto huur/jaar inschatting bij aankoop (PGI)",
      key: "potentialGrossIncomeActual",
      mask: "valuta",
    },
    {
      label:
        "Delta contractuele huren + ERV t.o.v. inschatting huur bij aankoop",
      key: "deltaRentalValueSinceAcquisition",
      mask: "valuta",
    },
    { label: "Actuele leegstaande units", key: "actualVacantUnits" },
  ];

  const financialFieldsPaymentsSuppliers = [
    { label: "Te ontvangen van leveranciers", key: "suppliersReceivables" },
    { label: "Dagen tot betaling huurder", key: "daysToPaySuppliers" },
    { label: "Huurdersvorderingen 30 dagen", key: "suppliersNotices30Days" },
    { label: "Huurdersvorderingen 60 dagen", key: "suppliersNotices60Days" },
    { label: "Huurdersvorderingen 90 dagen", key: "suppliersNotices90Days" },
    { label: "Huurdersvorderingen langer", key: "suppliersNoticesExtended" },
    { label: "Vorderingen gemiddelde looptijd", key: "suppliersNoticesMedian" },
  ];

  const sharedSectionProps = {
    editMode: false,
    isLoading,
    originalState: { ...buildingFinancials, ...buildingAcquisitionInfo },
    editedState: buildingFinancials,
    setStateFunction: setBuildingFinancials,
  };

  const sharedSectionPropsAcquisition = {
    editMode: buildingIsOwned && acquisitionEditMode,
    isLoading: buildingAcquisitionInfoLoading,
    onEditClick: toggleAcquisitionEditMode,
    onSaveClick: saveBuildingAcquisitionInfo,
    originalState: buildingAcquisitionInfo,
    editedState: editedBuildingAcquisitionInfo,
    setStateFunction: setEditedBuildingAcquisitionInfo,
  };

  return (
    <>
      <div className="sticky top-0 z-20 mb-4 rounded-lg bg-moss-primary/10 p-3">
        <Select
          placeholder="Selecteer jaar"
          value={selectedYear}
          onChange={handleYearChange}
          defaultSelectedKeys={["current"]}
          classNames={{
            label: "text-neutral-primary",
            value: "text-neutral-primary",
            trigger:
              "rounded-sm bg-white shadow-form-element data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv group-data-[focus=true]:shadow-form-element-fc",
          }}
        >
          <SelectItem key="all">
            Alle jaren
          </SelectItem>
          <SelectItem key="current">
            Dit jaar
          </SelectItem>
          <SelectItem key="previous">
            Vorig jaar
          </SelectItem>
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div className="flex flex-col gap-6">
          {renderSection({
            title: "Evaluatie vastgoed (Bij aankoop)",
            icon: <AlertInfo />,
            fieldsConfig: financialFieldsEvalProperty,
            ...sharedSectionPropsAcquisition,
          })}
          {renderSection({
            title: "Benchmarks (Bij aankoop)",
            icon: <Target />,
            fieldsConfig: financialBenchmarks,
            ...sharedSectionPropsAcquisition,
          })}
          {renderSection({
            title: "Financiering (Bij aankoop)",
            icon: <Euro />,
            fieldsConfig: financingFields,
            ...sharedSectionPropsAcquisition,
          })}
        </div>
        <div className="flex flex-col gap-6">
          {renderSection({
            title: "Samenstelling gebouw",
            icon: <Incoming />,
            fieldsConfig: buildingComposureFields,
            ...sharedSectionPropsAcquisition,
          })}
          {renderSection({
            title: "Evaluatie huur (Bij aankoop)",
            icon: <AlertInfo />,
            fieldsConfig: financialFieldsEvalRent,
            ...sharedSectionPropsAcquisition,
          })}
        </div>
        <div className="flex flex-col gap-6">
          {renderSection({
            title: "Waarde vastgoed (Na aankoop)",
            icon: <Euro />,
            fieldsConfig: financialFieldsValueProperty,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Inkomsten en marges (Na aankoop)",
            icon: <Incoming />,
            fieldsConfig: financialFieldsIncomingMargins,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Assumpties voor taxaties (Na aankoop)",
            icon: <Target />,
            fieldsConfig: taxationBenchmarkFields,
            ...sharedSectionPropsAcquisition,
          })}
        </div>
        <div className="flex flex-col gap-6">
          {renderSection({
            title:
              "Actuele geïndexeerde contractuele huurinkomsten (excl. provisies) (Na aankoop)",
            icon: <Incoming />,
            fieldsConfig: actualIndexedContractualRents,
            ...sharedSectionPropsAcquisition,
          })}
        </div>
        <div>
          {renderSection({
            title: "Taxatie (Na aankoop)",
            icon: <Euro />,
            fieldsConfig: taxationFields,
            ...sharedSectionPropsAcquisition,
          })}
        </div>
        <div>
          {renderSection({
            title: "ROI (Na aankoop)",
            icon: <Pulse />,
            fieldsConfig: financialFieldsROI,
            ...sharedSectionPropsAcquisition,
          })}
        </div>

        <div>
          {renderSection({
            title: "Bezetting (Alleen huidig jaar)",
            icon: <Percentage />,
            fieldsConfig: financialFieldsOccupation,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Betalingen van huurders",
            icon: <Users />,
            fieldsConfig: financialFieldsPaymentsTenants,
            ...sharedSectionProps,
          })}
        </div>
        <div>
          {renderSection({
            title: "Te betalen aan leveranciers",
            icon: <Wrench />,
            fieldsConfig: financialFieldsPaymentsSuppliers,
            ...sharedSectionProps,
          })}
        </div>
      </div>
    </>
  );
};

export default FinancialsTab;
